import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-02 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/ielts_speaking_coach/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so practice history stays
// on-device. Context sent up is METADATA ONLY (session topics) — transcripts never
// ride along with chat. The keyword intent-router below is the FREE FALLBACK
// (offline / signed-out / out of funds) — chat never goes dead. Original prototype notes:
//
// It maps ONE user message → ONE ChatAction,
// reusing ONLY what THIS app already has (no new/edited server routes, no billed calls):
//   • "show/list (my) sessions", "find the travel one"      → local filter    → filter
//   • "how am I doing / my progress / band trend"           → local aggregate → progress
//   • "delete/remove the <topic> session"                   → resolve id locally → delete_session (UI confirms)
//   • anything else                                         → none + a short clarifying nudge
//
// Running a mock and getting feedback live on the main screen (they need the full spoken flow).

import { BrainError, brainCall } from "../brain";
import { findNamedMatches } from "./fuzzy";
import { store, normalizeTag, type Exam, type Mode, type Session } from "../store";
import { libraryFind, librarySummary } from "../library";
import type { ChatAction, SessionFilter, FilterRow } from "./actions";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — do everything asked, in order.
}

const HELP =
  "I can search your practice history, show your band trend, and delete sessions — just tell me. Try:\n" +
  '• "show my sessions" · "list IELTS mocks" · "find the travel one"\n' +
  '• "give me a part 2 cue card about travel" · "ask me some part 3 questions"\n' +
  '• "how am I doing?" · "what\'s my band trend?"\n' +
  '• "delete the hometown session"\n\n' +
  "To practice, use the mode cards on the main screen — the examiner speaks there and I'll be here after.";

const none = (message: string): BrainResponse => ({ reply: "", actions: [{ kind: "none", message }] });
const one = (say: string, action: ChatAction): BrainResponse => ({ reply: say, actions: [action] });

async function findByTopic(term: string): Promise<Session[]> {
  const q = term.trim().replace(/[.?!]+$/, "").replace(/\b(session|mock|practice|test)\b/gi, "").trim();
  if (!q) return [];
  const all = await store.listSessions({ limit: 50000 });
  // Pattern 6 — misspelling-tolerant, the way your memory search is: "the travle one" → Travel.
  const hits = findNamedMatches(all.map((r) => ({ name: r.topic, r })), q);
  return hits.map((h) => h.r);
}

function pickList(rows: Session[]): string {
  return rows.slice(0, 5).map((s) => `• ${s.topic || s.mode} (${s.answered_count}/${s.turn_count}, ${s.created_at.slice(0, 10)})`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return routeMessage(text);
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return none(e.message);
    }
    // Offline / transient server trouble → the free local keyword-router below
    // still answers; chat never goes dead.
    const r = await routeMessage(text);
    // A silent fallback cost a whole debugging session (2026-08-12), so the
    // reason is always NAMED — to the console for the operator, and on screen
    // only in a dev build. Customers just get the working local answer.
    const tag = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    console.warn("[oriora] chat fell back to the local router:", tag);
    if (import.meta.env.VITE_ORIORA_DIAG === "1") {
      return { ...r, reply: (r.reply ? r.reply + "\n" : "") + `[fallback: ${tag.slice(0, 120)}]` };
    }
    return r;
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (topic → session id) locally,
// because the practice history lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.ielts-speaking-coach.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

/** Exported so the LIVE VOICE session can declare the SAME powers the typed chat has —
 *  the server turns this catalogue into the call's in-call tools at mint time. */
export const CHAT_ACTIONS = [
  { name: "filter", description: "Search / filter the user's practice-session history.", params: { text: "optional free text the session topic contains", exam: "optional exam: ielts | toefl", mode: "optional mode: mock | interview | listen_repeat", in_progress: "optional true — only unfinished sessions (unanswered questions left)" } },
  { name: "progress", description: "Show the user's overall progress — sessions practiced and estimated band trend." },
  { name: "delete_session", description: "Delete one practice session identified by its topic (the app asks the user to confirm first).", params: { topic: "which session (its practice topic, from APP DATA)" } },
  { name: "update_session", description: "RETITLE a practice session — give it a clearer topic name. Use for 'rename/call that session X', 'change the topic of...'. Nothing recorded in the session is altered, only its label.", params: { topic: "REQUIRED — which session, by its CURRENT topic (from APP DATA)", new_topic: "REQUIRED — the new topic name" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole collection — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "practice_questions", description: "Give the student real practice questions to answer out loud, from the examiner. Use for 'give me a part 2 cue card about travel', 'ask me some part 3 questions', 'interview questions about my job'.", params: { exam: "ielts or toefl (default ielts)", part: "p1, p2, p3 or interview", topic: "optional topic, as the student said it" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── the LOCAL lesson library (device-only; the customer's own material) ─────
  { name: "library_save", description: "Save a lesson, study plan or note into the customer's ON-DEVICE library. When they ask you to RESEARCH something and make/save a plan or lesson: write the FULL plan/lesson in `content` (use the web results in the message when present), give it a clear title, and ALSO put a short summary in your reply. Use for 'save that as a lesson', 'make me a study plan and keep it', 'add a note to my library'.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text of the lesson/plan/note — write it out completely", folder: "optional folder name, e.g. Plans, Grammar" } },
  { name: "library_list", description: "List what's in the customer's library ('what lessons do I have', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the user to confirm first). LIBRARY items only — sessions have delete_session.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the grammar lesson to Basics').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one ('merge my two pronunciation lessons'). The app asks the user to confirm first.", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The server sends BOTH `actions` (the truth) and back-compat `action`/`params` = actions[0].
// Reading only the singular field means "do X and Y" silently does X. (G17)
interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  // Metadata only — session topics so "delete the travel one" resolves. The
  // transcripts (the user's own spoken answers) NEVER ride along with chat.
  const sessions = await store.listSessions({ limit: 40 });
  const topics = Array.from(new Set(sessions.map((s) => s.topic.trim()).filter(Boolean)));
  const _notes = getInstructions();
  const context = (`Session topics (most recent first): ${topics.join(", ") || "(none yet)"}. ` +
    `LIBRARY (the customer's saved lessons/plans/notes): ${librarySummary()}. ` +
    "You can also hand the student real practice questions right here in the chat (practice_questions); a full timed mock still starts from the mode cards.") + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);

  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);
  if (!raw.length) return none(say || HELP);   // "actions": [] = a question, decline or clarification

  const actions: ChatAction[] = [];
  for (const a of raw) {
    const step = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(...step.actions);             // a per-item "none" carries its own explanation
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asFlag = (v: unknown): boolean => v === true || /^(true|yes|1)$/i.test(String(v ?? "").trim());
const EXAMS = new Set<Exam>(["ielts", "toefl"]);
const MODES = new Set<Mode>(["mock", "interview", "listen_repeat"]);

// Resolve a topic reference to exactly one local session, or explain why not.
async function resolveOne(term: string | undefined): Promise<{ row?: Session; problem?: string }> {
  if (!term) return { problem: "Which session do you mean? Give me its topic." };
  const rows = await findByTopic(term);
  if (rows.length === 0) return { problem: `I couldn't find a session matching "${term}".` };
  if (rows.length > 1) return { problem: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` };
  return { row: rows[0] };
}

/** Exported for chat/voiceTools.ts — a live voice tool call is (name, args) and needs the
 *  SAME mapping the typed chat uses, so voice can never drift from chat. */
export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";
  switch (r.action) {
    case "filter": {
      // An empty filter is legitimate here — "show my sessions" lists everything.
      const filter: SessionFilter = {};
      const text = asStr(p.text);
      if (text) filter.text = normalizeTag(text);
      const exam = asStr(p.exam)?.toLowerCase() as Exam | undefined;
      if (exam && EXAMS.has(exam)) filter.exam = exam;
      const mode = asStr(p.mode)?.toLowerCase().replace(/[\s&-]+/g, "_") as Mode | undefined;
      if (mode && MODES.has(mode)) filter.mode = mode;
      if (asFlag(p.in_progress)) filter.inProgress = true;
      return { reply: "", actions: [{ kind: "filter", filter, label: say || undefined }] };
    }
    case "progress":
      return { reply: say, actions: [{ kind: "progress" }] };
    case "delete_session": {
      const { row, problem } = await resolveOne(asStr(p.topic));
      if (!row) return none(problem!);
      return { reply: say, actions: [{ kind: "delete_session", id: row.id, label: `the "${row.topic || row.mode}" session` }] };
    }
    case "update_session": {
      const next = asStr(p.new_topic);
      if (!next) return none(say || "What should I rename it to?");
      const { row, problem } = await resolveOne(asStr(p.topic));
      if (!row) return none(problem!);
      return { reply: say, actions: [{ kind: "update_session", id: row.id, topic: next, label: `the "${row.topic || row.mode}" session` }] };
    }
    case "backup_save":
      return { reply: say, actions: [{ kind: "backup_save" }] };
    case "backup_status":
      return { reply: say, actions: [{ kind: "backup_status" }] };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, actions: [{ kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined }] };
    }
    case "save_local_copy":
      return { reply: say, actions: [{ kind: "save_local_copy" }] };
    case "import_file":
      return { reply: say, actions: [{ kind: "import_file" }] };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, actions: [{ kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination }] };
    }
    case "practice_questions": {
      const examStr = (asStr(p.exam) ?? "ielts").toLowerCase();
      const exam = (EXAMS.has(examStr as Exam) ? examStr : "ielts") as Exam;
      const partStr = (asStr(p.part) ?? "").toLowerCase().replace(/^part\s*/, "p");
      const part = (["p1", "p2", "p3", "interview"].includes(partStr) ? partStr : "p1") as "p1" | "p2" | "p3" | "interview";
      return one(say, { kind: "practice_questions", exam, part, topic: asStr(p.topic) });
    }

    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, actions: [{ kind: "remember", instruction }] };
    }

    // ── the LOCAL lesson library (agent layer, 2026-08-12) ──────────────────
    case "library_save": {
      const content = asStr(p.content);
      if (!content) return none("Tell me what to save — or ask me to write the lesson first and I'll keep it.");
      const rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
      const itemKind = rawKind === "lesson" || rawKind === "plan" ? rawKind : "note";
      return { reply: say, actions: [{ kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind, content, folder: asStr(p.folder) }] };
    }
    case "library_list":
      return { reply: say, actions: [{ kind: "library_list", folder: asStr(p.folder) }] };
    case "library_remove": {
      const { item, problem } = resolveLibraryItem(asStr(p.title));
      if (!item) return none(say || problem!);
      return { reply: say, actions: [{ kind: "library_remove", id: item.id, label: `"${item.title}"` }] };
    }
    case "library_move": {
      const folder = asStr(p.folder);
      if (!folder) return none("Which folder should it go to?");
      const { item, problem } = resolveLibraryItem(asStr(p.title));
      if (!item) return none(say || problem!);
      return { reply: say, actions: [{ kind: "library_move", id: item.id, folder, label: `"${item.title}"` }] };
    }
    case "library_merge": {
      const titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
      if (titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
      const ids: string[] = [];
      for (const t of titles) {
        const { item, problem } = resolveLibraryItem(t);
        if (!item) return none(problem!);
        ids.push(item.id);
      }
      const title = asStr(p.new_title) ?? titles.join(" + ");
      return { reply: say, actions: [{ kind: "library_merge", ids, title, label: titles.map((t) => `"${t}"`).join(" + ") }] };
    }

    default:
      return none(say || HELP);
  }
}

// Resolve a library title reference to exactly one item, or explain why not.
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which library item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}

// ---- LOCAL KEYWORD ROUTER (the free fallback) -------------------------------

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) PROGRESS — local aggregation over the user's own history (no billed call).
  if (
    /\bhow\s+am\s+i\s+doing\b/i.test(low) ||
    /\b(my\s+)?progress\b/i.test(low) ||
    /\bband\s*(trend|score|history)\b/i.test(low) ||
    /\bam\s+i\s+(getting\s+better|improving)\b/i.test(low)
  ) {
    return { reply: "", actions: [{ kind: "progress" }] };
  }

  // 2) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?(?:session\s+|mock\s+|practice\s+)?(?:for\s+|called\s+|about\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findByTopic(term);
    if (rows.length === 0) return none(`I couldn't find a session matching "${term}".`);
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", actions: [{ kind: "delete_session", id: rows[0].id, label: `the "${rows[0].topic || rows[0].mode}" session` }] };
  }

  // 3) FILTER / SEARCH — query-shaped → local filter over the on-device history.
  if (/\b(show|find|list|search|which|filter)\b/i.test(low)) {
    const filter = parseFilter(t);
    return { reply: "", actions: [{ kind: "filter", filter, label: t }] };
  }

  // 4) "start a practice"-shaped asks → point at the main screen (the flow needs the spoken loop).
  if (/\b(start|new|begin|practice|mock|interview|question|repeat)\b/i.test(low)) {
    return none("Use the mode cards on the main screen — IELTS mock, TOEFL interview, or Listen & Repeat. The examiner speaks there; I can search your history and show your band trend here.");
  }

  // 5) Unclear → a short clarifying nudge.
  return none(HELP);
}

/** Build a local SessionFilter from free text — exam, mode, in-progress, topic text. */
function parseFilter(text: string): SessionFilter {
  const low = text.toLowerCase();
  const f: SessionFilter = {};
  if (/\b(unfinished|in\s*progress|incomplete|open)\b/i.test(low)) f.inProgress = true;
  if (/\bielts\b/i.test(low)) f.exam = "ielts";
  else if (/\btoefl\b/i.test(low)) f.exam = "toefl";
  if (/\bmock/i.test(low)) f.mode = "mock";
  else if (/\binterview/i.test(low)) f.mode = "interview";
  else if (/\b(listen|repeat)/i.test(low)) f.mode = "listen_repeat";
  // Topic text: "find/show ... <term> (session|sessions|one)"
  const m = text.match(/\b(?:find|show|search)\s+(?:me\s+)?(?:the\s+)?(.+?)(?:\s+(?:sessions?|mocks?|practices?|one))?\s*[.?!]*$/i);
  if (m) {
    const term = normalizeTag(m[1])
      .replace(/\b(my|all|sessions?|mocks?|practices?|list|of|ielts|toefl|interview|listen|repeat)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (term) f.text = term;
  }
  return f;
}
